/**
 * The feedback affordance, from the org `in-app-feedback` pattern.
 *
 * It sits directly beside the account area on every screen, which the pattern fixes and this
 * app has no reason to vary. What the app *does* vary is the default category and the context:
 * opened from a proposal, a claim or a budget screen it defaults to **Question** and attaches the
 * reference, because 2026 is the first year HR035 runs at all and most of what arrives here will
 * be "my budget looks wrong". A reference number is the difference between a complaint and a
 * reproducible case.
 *
 * The modal is one screen with two halves: raise something, and read what came back. Splitting
 * them would mean a reply nobody finds — the unseen badge points here and nowhere else.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatInstant } from '@/lib/dates';
import { Alert, Badge, Button, Dialog, Field, Select, Textarea } from '@/components/ui';
import { ErrorNote } from '@/components/bits';
import type { Tone } from '@/lib/policy';
import type { FeedbackCategory, FeedbackContext, FeedbackStatus } from '@/lib/types';

export const FEEDBACK_STATUS: Record<FeedbackStatus, { label: string; tone: Tone }> = {
  new: { label: 'New', tone: 'progress' },
  in_progress: { label: 'In progress', tone: 'progress' },
  resolved: { label: 'Resolved', tone: 'good' },
  declined: { label: 'Declined', tone: 'neutral' },
};

export const FEEDBACK_CATEGORY: Record<FeedbackCategory, string> = {
  bug: 'Something is broken',
  idea: 'An idea',
  question: 'A question',
  other: 'Something else',
};

/**
 * What the app knows about where you were.
 *
 * The path and the record id come from the router rather than being typed, which is the whole
 * point: nobody reports the URL they were on, and it is the first thing anybody investigating
 * asks for.
 */
function contextOf(pathname: string, appVersion: string): FeedbackContext {
  const proposal = /^\/proposals\/(\d+)/.exec(pathname);
  const claim = /^\/claims\/(\d+)/.exec(pathname);
  return {
    path: pathname,
    app_version: appVersion,
    ...(proposal?.[1] ? { proposal_id: Number(proposal[1]) } : {}),
    ...(claim?.[1] ? { claim_id: Number(claim[1]) } : {}),
  };
}

/** Budget, proposal and claim screens default to a question; everywhere else, to an idea. */
function defaultCategory(pathname: string): FeedbackCategory {
  return /^\/(proposals|claims|reports|hr\/budget)/.test(pathname) ? 'question' : 'idea';
}

export function FeedbackButton({ appVersion }: { appVersion: string }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const queryClient = useQueryClient();

  const mine = useQuery({
    queryKey: ['my-feedback'],
    queryFn: api.myFeedback,
    // Only while the modal is shut does this need to be cheap; it is the badge's source.
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
  });

  const unseen = mine.data?.unseen ?? 0;

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setOpen(true)}
        title="Tell HR about a bug, an idea, or a figure that looks wrong"
      >
        Feedback
        {unseen > 0 ? (
          <span className="bg-destructive text-destructive-foreground ml-1 min-w-4 rounded-full px-1 text-[0.65rem] leading-4 font-semibold">
            {unseen}
          </span>
        ) : null}
      </Button>

      <FeedbackDialog
        open={open}
        onClose={() => {
          setOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['my-feedback'] });
        }}
        pathname={location.pathname}
        appVersion={appVersion}
      />
    </>
  );
}

function FeedbackDialog({
  open,
  onClose,
  pathname,
  appVersion,
}: {
  open: boolean;
  onClose: () => void;
  pathname: string;
  appVersion: string;
}) {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<FeedbackCategory>(() => defaultCategory(pathname));
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);

  // The default follows the screen it was opened from, so re-opening elsewhere re-defaults.
  useEffect(() => {
    if (open) {
      setCategory(defaultCategory(pathname));
      setSent(false);
    }
  }, [open, pathname]);

  const context = useMemo(() => contextOf(pathname, appVersion), [pathname, appVersion]);

  const mine = useQuery({ queryKey: ['my-feedback'], queryFn: api.myFeedback, enabled: open });

  const raise = useMutation({
    mutationFn: () => api.raiseFeedback({ category, body, context }),
    onSuccess: () => {
      setBody('');
      setSent(true);
      void queryClient.invalidateQueries({ queryKey: ['my-feedback'] });
    },
  });

  const seen = useMutation({
    mutationFn: (id: number) => api.markFeedbackSeen(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-feedback'] }),
  });

  const items = mine.data?.feedback ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Feedback"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            busy={raise.isPending}
            disabled={body.trim() === ''}
            onClick={() => raise.mutate()}
          >
            Send
          </Button>
        </>
      }
    >
      {raise.error ? <ErrorNote error={raise.error} /> : null}
      {sent ? (
        <Alert tone="good">
          Sent. HR sees it in the morning digest, and you will see the reply here — the button shows
          a badge when there is one.
        </Alert>
      ) : null}

      <Field
        label="What is this about?"
        hint="Required. It decides who looks at it first."
        required
      >
        {(props) => (
          <Select
            {...props}
            value={category}
            onChange={(event) => setCategory(event.target.value as FeedbackCategory)}
          >
            {(Object.keys(FEEDBACK_CATEGORY) as FeedbackCategory[]).map((key) => (
              <option key={key} value={key}>
                {FEEDBACK_CATEGORY[key]}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label="Tell HR what happened"
        hint={
          context.proposal_id
            ? `The page you are on (proposal ${context.proposal_id}) is attached automatically.`
            : context.claim_id
              ? `The page you are on (claim ${context.claim_id}) is attached automatically.`
              : 'The page you are on is attached automatically.'
        }
      >
        {(props) => (
          <Textarea
            {...props}
            rows={4}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="A figure that looks wrong, a step that did not work, or something that would help."
          />
        )}
      </Field>

      {items.length > 0 ? (
        <div className="border-border border-t pt-4">
          <p className="mb-2 text-sm font-medium">What you have raised</p>
          <ul className="grid gap-2">
            {items.map((item) => (
              <li key={item.id} className="border-border rounded-md border px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={FEEDBACK_STATUS[item.status].tone}>
                    {FEEDBACK_STATUS[item.status].label}
                  </Badge>
                  <span className="text-muted-foreground text-xs">
                    {FEEDBACK_CATEGORY[item.category]} · {formatInstant(item.created_at)}
                  </span>
                  {item.raiser_unseen ? (
                    <button
                      type="button"
                      className="text-primary ml-auto text-xs underline underline-offset-2"
                      onClick={() => seen.mutate(item.id)}
                    >
                      new reply — mark read
                    </button>
                  ) : null}
                </div>
                <p className="mt-1 text-sm">{item.body}</p>
                {item.admin_remark ? (
                  <p className="text-muted-foreground mt-1 border-l-2 pl-2 text-sm">
                    HR: {item.admin_remark}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Dialog>
  );
}
